import { useState } from "react";
import toast from "react-hot-toast";
import { FaUser } from "react-icons/fa6";
import { MdKeyboardArrowDown } from "react-icons/md";
import { PiTruckTrailerDuotone } from "react-icons/pi";
import { TenstreetApplicant } from "src/types/tenstreet-applicant";
import { DriverSearch } from "../components/driver-search";
import { ExamHistoryButton } from "../components/exam-history-button";
import { Exam } from "../components/exam/exam";
import { SavedExam } from "../types/exam";
import { ExamType } from "../types/exam-type";
import { SelectedDriver } from "../types/selected-driver";
import { getFullName } from "../utils/get-full-name";
// import { useQuery } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import {
    driverRoadTestMutations,
    driverRoadTestQueryOptions,
} from "src/api/mvte/driver-road-test-query-options";
import useAccountEnhanced from "src/hooks/use-account-enhanced";

function SelectedDriverCard({
    driver,
    onStartExam,
    onRemove,
}: {
    driver: SelectedDriver;
    onStartExam: (driver: SelectedDriver, exam: SavedExam) => void;
    onRemove: (licenseNumber: string) => void;
}) {
    const account = useAccountEnhanced();
    const driverExams = useQuery({
        ...driverRoadTestQueryOptions.driverExamsQueryOptions({
            params: {
                licenseNumber: driver.driver.licenseNumber,
            },
        }),
    });

    const createExamMutation = driverRoadTestMutations.useCreateExam();

    const examInProgress: SavedExam | null =
        driverExams.data?.find(
            (driverExam) =>
                driverExam.examStatus !== "COMPLETED" &&
                driverExam.examType === driver.examType.examTypeName,
        ) ?? null;

    async function startExam(driver: SelectedDriver) {
        // Create the initial exam
        const initialExam = await createExamMutation.mutateAsync({
            body: {
                exam: {
                    licenseNumber: driver.driver.licenseNumber,
                    examType: driver.examType.examTypeName,
                    examinerEmployeeCode: account.me.employeeCode,
                },
            },
        });

        onStartExam(driver, initialExam);
    }

    function resumeExam(driver: SelectedDriver) {
        if (!examInProgress) return;
        onStartExam(driver, examInProgress);
    }

    const driverName = getFullName(
        driver.driver.firstName,
        driver.driver.middleName,
        driver.driver.lastName,
    );

    return (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
            <div className="flex items-center gap-3 min-w-0">
                <div className="bg-gray-100 rounded-full p-2 flex-shrink-0">
                    <FaUser className="w-4 h-4 text-gray-600" />
                </div>
                <div className="min-w-0">
                    <h3 className="font-medium text-gray-800 truncate">
                        {driverName}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                            {driver.driver.licenseNumber}
                        </span>
                        <span className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                            {driver.examType.examTypeName}
                        </span>
                    </div>
                </div>
            </div>

            <div className="flex items-center ml-4">
                <div className="flex items-center gap-1.5 pr-2 border-r border-gray-200">
                    {driverExams.isLoading ? (
                        <button
                            className="btn btn-ghost btn-sm h-8 min-h-0 px-2 loading"
                            disabled
                        >
                            <span className="loading loading-spinner loading-xs"></span>
                        </button>
                    ) : examInProgress === null ? (
                        <button
                            className="btn btn-primary btn-sm h-8 min-h-0 px-3"
                            onClick={() => startExam(driver)}
                        >
                            Start
                        </button>
                    ) : (
                        <button
                            className="btn btn-primary btn-sm h-8 min-h-0 px-3"
                            onClick={() => resumeExam(driver)}
                        >
                            Resume
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-1.5 pl-2">
                    <ExamHistoryButton
                        licenseNumber={driver.driver.licenseNumber}
                        driver={driver.driver}
                        onStartExam={onStartExam}
                    />
                    <button
                        className="btn btn-ghost btn-error btn-sm h-8 min-h-0 px-2"
                        onClick={() => onRemove(driver.driver.licenseNumber)}
                    >
                        Remove
                    </button>
                </div>
            </div>
        </div>
    );
}

function SelectedDriverList({
    selectedDrivers,
    onStartExam,
    removeDriver,
}: {
    selectedDrivers: SelectedDriver[];
    onStartExam: (driver: SelectedDriver, exam: SavedExam) => void;
    removeDriver: (licenseNumber: string) => void;
}) {
    if (selectedDrivers.length === 0) {
        return <DisplayNoDriversSelected />;
    }

    return (
        <div className="w-full max-w-2xl bg-white rounded-lg shadow-md border border-base-300 p-8">
            <div className="flex flex-col items-center justify-center mb-6">
                <div className="bg-gray-100 p-5 rounded-full mb-4">
                    <PiTruckTrailerDuotone className="w-16 h-16 text-[#ac2226]" />
                </div>
                <h2 className="text-xl font-bold text-gray-700 mb-4">
                    Selected Driver Exams
                </h2>

                <div className="w-full space-y-4">
                    {selectedDrivers.map((driver, index) => (
                        <SelectedDriverCard
                            key={index}
                            driver={driver}
                            onStartExam={onStartExam}
                            onRemove={removeDriver}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function IndexPage() {
    const {
        selectedDrivers,
        activeExam,
        addDriver,
        removeDriver,
        setActiveExam,
        updateExam,
        isExamCompleted,
        canExamBeCompleted,
    } = useDriverExamState();

    return (
        <div className="flex h-full bg-base-200" data-theme="corporate">
            <Sidebar selectedDrivers={selectedDrivers} addDriver={addDriver} />

            {/* Main content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                <div className="h-full flex flex-col">
                    <div className="flex-1 p-6 overflow-auto flex items-center justify-center">
                        {!activeExam ? (
                            <SelectedDriverList
                                selectedDrivers={selectedDrivers}
                                onStartExam={(driver, exam) =>
                                    setActiveExam({ driver, exam })
                                }
                                removeDriver={removeDriver}
                            />
                        ) : (
                            <Exam
                                exam={activeExam.exam}
                                driver={activeExam.driver.driver}
                                isExamCompleted={isExamCompleted}
                                onExamUpdate={updateExam}
                                onExit={() => {
                                    setActiveExam(null);
                                }}
                                canExamBeCompleted={canExamBeCompleted}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// Components
function Sidebar({
    selectedDrivers,
    addDriver,
}: {
    selectedDrivers: SelectedDriver[];
    addDriver: (driver: TenstreetApplicant, examType: ExamType) => void;
}) {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    return (
        <div
            className={`bg-base-100 border-r border-base-300 transition-all duration-300 flex flex-col ${
                sidebarCollapsed ? "w-[60px]" : "w-[320px]"
            }`}
        >
            {/* Logo and collapse button */}
            <div className="flex items-center justify-between p-4 border-b border-base-300">
                {!sidebarCollapsed && (
                    <div className="flex items-center">
                        <PiTruckTrailerDuotone className="w-8 h-8 mr-2 text-[#ac2226]" />

                        <span className="font-bold text-lg">
                            Driver Road Test
                        </span>
                    </div>
                )}
                <button
                    className="btn btn-ghost btn-sm btn-square"
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                >
                    {sidebarCollapsed ? (
                        <MdKeyboardArrowDown className="h-5 w-5 -rotate-90" />
                    ) : (
                        <MdKeyboardArrowDown className="h-5 w-5" />
                    )}
                </button>
            </div>

            {/* Sidebar content */}
            {!sidebarCollapsed && (
                <DriverSearch
                    onSelectDriver={addDriver}
                    selectedDrivers={selectedDrivers}
                />
            )}
        </div>
    );
}

function DisplayNoDriversSelected() {
    return (
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-md border border-base-300 p-8 text-center">
            <div className="flex flex-col items-center justify-center mb-6">
                <div className="bg-gray-100 p-5 rounded-full mb-4">
                    <PiTruckTrailerDuotone className="w-16 h-16 text-[#ac2226]" />
                </div>
                <h2 className="text-xl font-bold text-gray-700 mb-2">
                    No Drivers Selected Yet
                </h2>
                <p className="text-gray-600">
                    Use the sidebar to search and add drivers to this exam
                    session.
                </p>
            </div>
        </div>
    );
}

// Hooks
function useDriverExamState() {
    const [selectedDrivers, setSelectedDrivers] = useState<SelectedDriver[]>(
        [],
    );
    const [activeExam, setActiveExam] = useState<{
        driver: SelectedDriver;
        exam: SavedExam;
    } | null>(null);

    function addDriver(driver: TenstreetApplicant, examType: ExamType) {
        const newDriver: SelectedDriver = {
            driver,
            examType,
        };

        setSelectedDrivers([...selectedDrivers, newDriver]);

        toast.success(
            `Added ${driver.firstName} ${driver.lastName} to selected drivers`,
        );
    }

    function removeDriver(licenseNumber: string) {
        setSelectedDrivers(
            selectedDrivers.filter(
                ({ driver }) => driver.licenseNumber !== licenseNumber,
            ),
        );
    }

    function updateExam(updatedExam: SavedExam) {
        if (activeExam) {
            setActiveExam({
                ...activeExam,
                exam: updatedExam,
            });
        }
    }

    function checkCanExamBeCompleted(): boolean {
        // Check if all sections are completed
        const areAllSectionsCompleted = !!activeExam?.exam.sections.every(
            (section) => section.status === "completed",
        );

        const hasPowerUnit = !!activeExam?.exam.typeOfPowerUnit;
        const hasTrailer = !!activeExam?.exam.typeOfTrailer;
        const hasMilesDriven = !!activeExam?.exam.milesDriven;

        const hasAllRequirements =
            areAllSectionsCompleted &&
            hasPowerUnit &&
            hasTrailer &&
            hasMilesDriven;

        return hasAllRequirements;
    }

    const canExamBeCompleted = checkCanExamBeCompleted();

    return {
        selectedDrivers,
        activeExam,
        addDriver,
        removeDriver,
        setActiveExam,
        updateExam,
        isExamCompleted: activeExam?.exam.examStatus === "COMPLETED",
        canExamBeCompleted,
    };
}

export { IndexPage };
